import {memo, useMemo} from "react";
import {useDispatch, useSelector} from "react-redux";
import {css, useTheme} from "@emotion/react";

import {calculateRemSize, flex, mb, ml, mt, mv, onDesktop, p, pt} from "@pg-design/helpers-css";
import {MapMarkerOutlineIcon, PoiBusIcon, PoiSubwayIcon, PoiTrainIcon, PoiTramIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {IRPStore} from "../../../app/rp_reducer";
import {setActivePoiRoute} from "../../../maps/actions/set_poi_travel_directions";
import {PoiItemWithLabel} from "../../../maps/components/PoiItemWithLabel";
import {RegionCenterDriveTime} from "../../../maps/components/RegionCenterDriveTime";
import {RegionTransportRoutes} from "../../../maps/components/RegionTransportRoutes";
import {useTransportPoisStats} from "../../../maps/hooks/use_transport_pois";
import {hitGtmBusRouteClick} from "../../../maps/tracking/routes_gtm_events";
import {IPublicTransportRoute} from "../../../maps/types/IPublicTransportRoute";
import {getHasCountryPois} from "../../../maps/utils/get_has_country_pois";
import {getPoiPluralizeLabel, PoiType} from "../../../maps/utils/PoiType";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {IOfferDetail} from "../../types/IOfferDetail";

interface IProps {
    offer: IOfferDetail | IPropertyDetailOffer;
    routes?: {
        busRoutes: IPublicTransportRoute[] | null;
        railwayRoutes: IPublicTransportRoute[] | null;
        subwayRoutes: IPublicTransportRoute[] | null;
        tramRoutes: IPublicTransportRoute[] | null;
    };
}

export const OfferDetailLocationSummary = memo((props: IProps) => {
    const {offer, routes} = props;

    const dispatch = useDispatch();
    const theme = useTheme();

    const poisStats = useSelector((state: IRPStore) => state.offerDetail.poisStats);
    const activePoiRoute = useSelector((state: IRPStore) => state.maps.travelDirections.activePoiRoute);
    const {hasTransportPoisStats, subwayPoi, railwayPoi, tramPoi, busPoi} = useTransportPoisStats();
    const poisCountLabels = useMemo(() => {
        if (!poisStats || !poisStats.poi?.nearest_pois_count) {
            return [] as string[];
        }

        const poiCounts = poisStats.poi.nearest_pois_count;
        const poiKeys = (Object.keys(poiCounts) as PoiType[]).filter((key) => ![PoiType.TRANSPORT, PoiType.ALL].includes(key));
        return poiKeys.reduce((acc, key) => {
            const poiTypeCount = poiCounts[key]?.count ?? 0;
            const poiCountLabel = poiTypeCount ? getPoiPluralizeLabel(key, poiTypeCount) : [];
            return acc.concat(poiCountLabel);
        }, [] as string[]);
    }, [poisStats]);

    const hasOtherPois = !!poisCountLabels.length;
    const hasCountryPois = getHasCountryPois(offer.region.country);

    const onTransportRouteClick = (poiRoute: IPublicTransportRoute) => {
        dispatch(setActivePoiRoute(poiRoute.id === activePoiRoute?.id ? null : poiRoute));
        hitGtmBusRouteClick();
    };

    return hasCountryPois ? (
        <div data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_PUBLIC_TRANSPORT_SECTION}>
            <div css={poiHolder}>
                {hasTransportPoisStats && (
                    <div css={poiHolderColumn}>
                        {subwayPoi && (
                            <PoiItemWithLabel
                                icon={<PoiSubwayIcon size="2.4" wrapperSize="2" wrapperColor="transparent" />}
                                label="Metro - "
                                poi={subwayPoi}
                                showName
                            />
                        )}

                        {!subwayPoi && railwayPoi && (
                            <PoiItemWithLabel
                                icon={<PoiTrainIcon size="1.6" wrapperSize="2" wrapperColor="#9BD7FF" />}
                                label="Stacja kolejowa - "
                                poi={railwayPoi}
                                showName
                            />
                        )}

                        {tramPoi && (
                            <PoiItemWithLabel
                                icon={<PoiTramIcon size="1.6" wrapperSize="2" wrapperColor="#9BD7FF" />}
                                label="Przystanek Tramwajowy"
                                poi={tramPoi}
                            />
                        )}

                        {busPoi && (
                            <PoiItemWithLabel
                                icon={<PoiBusIcon size="1.6" wrapperSize="2" wrapperColor="#9BD7FF" />}
                                label="Przystanek Autobusowy"
                                poi={busPoi}
                            />
                        )}

                        <div css={regionCenterWrapper}>
                            <RegionCenterDriveTime offer={offer} />
                        </div>

                        {routes && (
                            <div css={[routesWrapper]}>
                                <Text as="div" variant="info_txt_1">
                                    Linie komunikacyjne w okolicy:
                                </Text>
                                <RegionTransportRoutes {...routes} activePoiRoute={activePoiRoute} onTransportRouteClick={onTransportRouteClick} />
                            </div>
                        )}
                    </div>
                )}

                {hasOtherPois && (
                    <div css={[poiHolderColumn, flex("normal", "flex-start")]}>
                        <MapMarkerOutlineIcon size="1.6" wrapperSize="2" wrapperColor={theme.colors.primary} />

                        <div>
                            <Text css={[ml(), mv(0)]} variant="body_copy_1">
                                <strong>W pobliżu 1 km</strong> od tej inwestycji znajdują się:
                            </Text>

                            <Text css={[ml(), mt(3), mb(0)]} variant="body_copy_1">
                                {poisCountLabels.join(", ")}
                            </Text>
                        </div>
                    </div>
                )}
            </div>
        </div>
    ) : null;
});

const poiHolder = css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    ${p(2, 0, 0)};

    ${onDesktop(css`
        ${p(3, 3, 0, 3)};
        flex-direction: row;
        justify-content: space-between;
        gap: ${calculateRemSize(3)};
    `)};
`;

const poiHolderColumn = css`
    ${mb(3)};

    ${onDesktop(css`
        flex: 0 1 50%;
    `)};
`;

const regionCenterWrapper = () => css`
    min-height: 2.6rem;
    padding-left: 2.8rem; // icon width + margin
`;

const routesWrapper = () => css`
    ${pt(1.5)};
    padding-left: 2.8rem; // icon width + margin
`;
import {memo, useMemo} from "react";
import {useDispatch, useSelector} from "react-redux";
import {css, useTheme} from "@emotion/react";

import {calculateRemSize, flex, mb, ml, mt, mv, onDesktop, p, pt} from "@pg-design/helpers-css";
import {MapMarkerOutlineIcon, PoiBusIcon, PoiSubwayIcon, PoiTrainIcon, PoiTramIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {IRPStore} from "../../../app/rp_reducer";
import {setActivePoiRoute} from "../../../maps/actions/set_poi_travel_directions";
import {PoiItemWithLabel} from "../../../maps/components/PoiItemWithLabel";
import {RegionCenterDriveTime} from "../../../maps/components/RegionCenterDriveTime";
import {RegionTransportRoutes} from "../../../maps/components/RegionTransportRoutes";
import {useTransportPoisStats} from "../../../maps/hooks/use_transport_pois";
import {hitGtmBusRouteClick} from "../../../maps/tracking/routes_gtm_events";
import {IPublicTransportRoute} from "../../../maps/types/IPublicTransportRoute";
import {getHasCountryPois} from "../../../maps/utils/get_has_country_pois";
import {getPoiPluralizeLabel, PoiType} from "../../../maps/utils/PoiType";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {IOfferDetail} from "../../types/IOfferDetail";

interface IProps {
    offer: IOfferDetail | IPropertyDetailOffer;
    routes?: {
        busRoutes: IPublicTransportRoute[] | null;
        railwayRoutes: IPublicTransportRoute[] | null;
        subwayRoutes: IPublicTransportRoute[] | null;
        tramRoutes: IPublicTransportRoute[] | null;
    };
}

export const OfferDetailLocationSummary = memo((props: IProps) => {
    const {offer, routes} = props;

    const dispatch = useDispatch();
    const theme = useTheme();

    const poisStats = useSelector((state: IRPStore) => state.offerDetail.poisStats);
    const activePoiRoute = useSelector((state: IRPStore) => state.maps.travelDirections.activePoiRoute);
    const {hasTransportPoisStats, subwayPoi, railwayPoi, tramPoi, busPoi} = useTransportPoisStats();
    const poisCountLabels = useMemo(() => {
        if (!poisStats || !poisStats.poi?.nearest_pois_count) {
            return [] as string[];
        }

        const poiCounts = poisStats.poi.nearest_pois_count;
        const poiKeys = (Object.keys(poiCounts) as PoiType[]).filter((key) => ![PoiType.TRANSPORT, PoiType.ALL].includes(key));
        return poiKeys.reduce((acc, key) => {
            const poiTypeCount = poiCounts[key]?.count ?? 0;
            const poiCountLabel = poiTypeCount ? getPoiPluralizeLabel(key, poiTypeCount) : [];
            return acc.concat(poiCountLabel);
        }, [] as string[]);
    }, [poisStats]);

    const hasOtherPois = !!poisCountLabels.length;
    const hasCountryPois = getHasCountryPois(offer.region.country);

    const onTransportRouteClick = (poiRoute: IPublicTransportRoute) => {
        dispatch(setActivePoiRoute(poiRoute.id === activePoiRoute?.id ? null : poiRoute));
        hitGtmBusRouteClick();
    };

    return hasCountryPois ? (
        <div data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_PUBLIC_TRANSPORT_SECTION}>
            <div css={poiHolder}>
                {hasTransportPoisStats && (
                    <div css={poiHolderColumn}>
                        {subwayPoi && (
                            <PoiItemWithLabel
                                icon={<PoiSubwayIcon size="2.4" wrapperSize="2" wrapperColor="transparent" />}
                                label="Metro - "
                                poi={subwayPoi}
                                showName
                            />
                        )}

                        {!subwayPoi && railwayPoi && (
                            <PoiItemWithLabel
                                icon={<PoiTrainIcon size="1.6" wrapperSize="2" wrapperColor="#9BD7FF" />}
                                label="Stacja kolejowa - "
                                poi={railwayPoi}
                                showName
                            />
                        )}

                        {tramPoi && (
                            <PoiItemWithLabel
                                icon={<PoiTramIcon size="1.6" wrapperSize="2" wrapperColor="#9BD7FF" />}
                                label="Przystanek Tramwajowy"
                                poi={tramPoi}
                            />
                        )}

                        {busPoi && (
                            <PoiItemWithLabel
                                icon={<PoiBusIcon size="1.6" wrapperSize="2" wrapperColor="#9BD7FF" />}
                                label="Przystanek Autobusowy"
                                poi={busPoi}
                            />
                        )}

                        <div css={regionCenterWrapper}>
                            <RegionCenterDriveTime offer={offer} />
                        </div>

                        {routes && (
                            <div css={[routesWrapper]}>
                                <Text as="div" variant="info_txt_1">
                                    Linie komunikacyjne w okolicy:
                                </Text>
                                <RegionTransportRoutes {...routes} activePoiRoute={activePoiRoute} onTransportRouteClick={onTransportRouteClick} />
                            </div>
                        )}
                    </div>
                )}

                {hasOtherPois && (
                    <div css={[poiHolderColumn, flex("normal", "flex-start")]}>
                        <MapMarkerOutlineIcon size="1.6" wrapperSize="2" wrapperColor={theme.colors.primary} />

                        <div>
                            <Text css={[ml(), mv(0)]} variant="body_copy_1">
                                <strong>W pobliżu 1 km</strong> od tej inwestycji znajdują się:
                            </Text>

                            <Text css={[ml(), mt(3), mb(0)]} variant="body_copy_1">
                                {poisCountLabels.join(", ")}
                            </Text>
                        </div>
                    </div>
                )}
            </div>
        </div>
    ) : null;
});

const poiHolder = css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    ${p(2, 0, 0)};

    ${onDesktop(css`
        ${p(3, 3, 0, 3)};
        flex-direction: row;
        justify-content: space-between;
        gap: ${calculateRemSize(3)};
    `)};
`;

const poiHolderColumn = css`
    ${mb(3)};

    ${onDesktop(css`
        flex: 0 1 50%;
    `)};
`;

const regionCenterWrapper = () => css`
    min-height: 2.6rem;
    padding-left: 2.8rem; // icon width + margin
`;

const routesWrapper = () => css`
    ${pt(1.5)};
    padding-left: 2.8rem; // icon width + margin
`;
